import { Modal } from './ui';

// Keyboard-shortcut cheatsheet. Opened from the global `?` / Cmd+/ handler in
// AppShell. Purely informational — lists shortcuts that live elsewhere; keep
// this in sync when you add a binding.
const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
const mod = isMac ? '⌘' : 'Ctrl';

type Row = { keys: string[]; label: string };

const SECTIONS: { title: string; rows: Row[] }[] = [
  {
    title: 'Navigation',
    rows: [
      { keys: [mod, 'K'], label: 'Open command palette (jump to channel / DM)' },
      { keys: ['?'], label: 'Show this shortcuts list' },
      { keys: ['Esc'], label: 'Close popovers, cancel reply / edit' },
    ],
  },
  {
    title: 'Voice (while in a call)',
    rows: [
      { keys: [mod, 'Shift', 'M'], label: 'Mute / unmute microphone' },
      { keys: [mod, 'Shift', 'D'], label: 'Deafen / undeafen' },
    ],
  },
  {
    title: 'Messages',
    rows: [
      { keys: ['Enter'], label: 'Send message' },
      { keys: ['Shift', 'Enter'], label: 'New line' },
      { keys: ['Right-click'], label: 'Message actions (reply, copy, pin, edit, delete)' },
      { keys: ['@'], label: 'Mention a member' },
      { keys: [':'], label: 'Insert a custom emoji' },
    ],
  },
  {
    title: 'Text formatting',
    rows: [
      { keys: ['**bold**'], label: 'Bold' },
      { keys: ['*italic*'], label: 'Italic' },
      { keys: ['~~strike~~'], label: 'Strikethrough' },
      { keys: ['||spoiler||'], label: 'Spoiler (click to reveal)' },
      { keys: ['`code`'], label: 'Inline code' },
      { keys: ['```'], label: 'Code block' },
    ],
  },
];

function Keycap({ children }: { children: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-6 px-1.5 py-0.5 text-[11px] font-mono font-semibold text-text-muted bg-elevated border border-border-soft rounded-sm shadow-[0_1px_0_rgba(0,0,0,0.12)]">
      {children}
    </kbd>
  );
}

export function ShortcutsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} title="Keyboard shortcuts" width="w-[460px]">
      <div className="space-y-4">
        {SECTIONS.map((section) => (
          <div key={section.title}>
            <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-1.5">
              {section.title}
            </div>
            <div className="space-y-1">
              {section.rows.map((row, i) => (
                <div key={i} className="flex items-center justify-between gap-4 py-0.5">
                  <span className="text-sm text-text-muted">{row.label}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    {row.keys.map((k, j) => (
                      <Keycap key={j}>{k}</Keycap>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
